/**
 * Faith 1st Consulting — firm-first B2B sales lobby content system.
 * All site copy lives here so layout components stay presentational.
 * Naming rule: the firm is ALWAYS "Faith 1st Consulting" (never spelled out).
 */

/* Brand identity */
export const brand6 = {
  legalName: "Faith 1st Consulting",
  wordmark: "Faith 1st Consulting",
  monogram: "F1C",
  category: "Leadership, People & Culture Advisory",
  tagline: "Steady counsel for high-stakes people, leadership, and workplace moments.",
  linkedin: "https://www.linkedin.com/in/katrina-bernard-jd-cpc-255279a/",
  email: "hello@faith1stconsulting.com",
  ctaLabel: "Book a consultation",
  siteUrl: "https://faith1st.coppervine.studio",
  founderPortrait:
    "https://customer-assets-v7afamib.emergentagent.net/job_calm-authority-site/artifacts/aq69e5c9_Untitled%20design%20%2815%29.webp",
};

/* Primary navigation */
export const nav6 = [
  { label: "Services", href: "/#services" },
  { label: "About", href: "/about-katrina" },
  { label: "Speaking & Keynotes", href: "/about-katrina#speaking" },
  { label: "Resources", href: "/resources" },
  { label: "Process", href: "/#process" },
  { label: "Contact", href: "/contact" },
];

/* Footer columns */
export const footerColumns6 = [
  [
    "Services",
    [
      ["HR & People Consulting", "/services/hr-people-consulting"],
      ["Strategic People & Leadership Advisory", "/services/strategic-people-leadership-advisory"],
      ["Management Training & Workshops", "/services/management-training-workshops"],
      ["Coaching for Leaders & Teams", "/services/coaching-leaders-teams"],
    ],
  ],
  [
    "Firm",
    [
      ["About", "/about-katrina"],
      ["About the Founder", "/about-katrina#founder"],
      ["Speaking & Keynotes", "/about-katrina#speaking"],
      ["Resources", "/resources"],
      ["Process", "/#process"],
      ["Contact", "/contact"],
    ],
  ],
  [
    "Connect",
    [
      ["Book a consultation", "/contact"],
      ["hello@faith1stconsulting.com", "mailto:hello@faith1stconsulting.com"],
      ["LinkedIn", "https://www.linkedin.com/in/katrina-bernard-jd-cpc-255279a/"],
      ["Washington, D.C. area", null],
    ],
  ],
];

/* -------------------------------------------------------------
 * SECTION 1 — Hero (firm-first)
 * ----------------------------------------------------------- */
export const hero6 = {
  eyebrow: "Faith 1st Consulting · Leadership, People & Culture Advisory",
  headline: "Steady counsel for high-stakes people, leadership, and workplace moments.",
  subhead:
    "Faith 1st Consulting helps mission-driven, values-led, and public-sector-adjacent organizations navigate the people, culture, leadership, and organizational moments that cannot be managed through policy alone.",
  primaryCta: { label: "Book a consultation", href: "/contact" },
  secondaryCta: { label: "Explore services", href: "/#services" },
  proofBadges: [
    {
      label: "The practice",
      body: "A senior advisory firm at the intersection of employment law, HR leadership, coaching, and facilitation.",
    },
    {
      label: "Who we serve",
      body: "Mission-driven, values-led, public-sector-adjacent, and mid-sized organizations — and the leaders who carry them.",
    },
    {
      label: "How it is held",
      body: "Confidential by default. Scoped in writing. Led with senior judgment throughout.",
    },
  ],
};

/* -------------------------------------------------------------
 * SECTION 2 — Recognition / buyer situations
 * ----------------------------------------------------------- */
export const situations6 = {
  eyebrow: "When organizations reach out",
  headline: "The moments that bring leaders to Faith 1st Consulting.",
  tiles: [
    { n: "01", tag: "Sensitive matters", title: "A sensitive people or workplace matter needs steady handling." },
    { n: "02", tag: "Change & transition", title: "Leaders need support through change, transition, or tension." },
    { n: "03", tag: "Manager capability", title: "Managers need more capability and confidence." },
    { n: "04", tag: "People function", title: "The people function needs senior-level structure or support." },
    { n: "05", tag: "Coaching", title: "A leader or team needs strengths-based coaching." },
  ],
};

/* -------------------------------------------------------------
 * SECTION 3 — Four service pillars (2×2 grid)
 * Each sub-service lives under exactly one pillar. `focusAreas` are
 * the short, keyword-led terms shown on cards; `subServices` carry the
 * fuller descriptions on the detail page; `boundary` states where the
 * pillar stops and the neighbouring pillar begins.
 * ----------------------------------------------------------- */
export const pillars6 = [
  {
    n: "01",
    slug: "hr-people-consulting",
    label: "HR & People Consulting",
    short: "HR & People",
    problem: "A sensitive people or workplace matter needs steady, structured handling.",
    outcome:
      "Hands-on HR delivery for the matters an organization cannot afford to mishandle.",
    focusAreas: [
      "Policy & handbook review",
      "Performance management",
      "Workplace investigations",
      "Conflict facilitation",
      "Sensitive employee matters",
      "HR project delivery",
    ],
    subServices: [
      ["Policy Review & Drafting", "Handbooks and policies reviewed and drafted for how the organization actually works."],
      ["Performance Management Support", "Frameworks and hands-on support so performance conversations actually happen — and hold."],
      ["Workplace Investigations", "Fair, discreet fact-finding with clear, defensible, usable findings."],
      ["Conflict & Sensitive Matters Facilitation", "Facilitated conversations for the moments teams cannot hold alone."],
      ["HR Project Management", "Structured, accountable delivery of defined people-related projects — owned end to end."],
    ],
    boundary:
      "This pillar is delivery: a defined matter, handled. When the need is standing senior counsel rather than a scoped project, it belongs in Strategic People & Leadership Advisory.",
    whoItsFor:
      "Organizations facing a specific people or workplace matter — a policy overdue for revision, a performance process that has stalled, a conflict that has outgrown the room, or a concern that needs fair, discreet fact-finding.",
    engagementShape: [
      "A confidential scoping conversation to understand the matter and its stakes",
      "A written scope — what will be done, by when, and within what boundaries",
      "Hands-on delivery, held with discretion and steady communication",
      "Clear findings, drafts, or recommendations the internal team can carry forward",
    ],
    detailIntro:
      "Some matters need more than advice — they need a senior, structured pair of hands. HR & People Consulting is the delivery arm of the practice: scoped projects with clear boundaries, handled with the discretion consequential people work requires.",
  },
  {
    n: "02",
    slug: "strategic-people-leadership-advisory",
    label: "Strategic People & Leadership Advisory",
    short: "Strategic Advisory",
    problem: "Leadership needs senior counsel through change, growth, or organizational tension.",
    outcome:
      "Senior counsel for the people decisions that shape structure, strategy, and change.",
    focusAreas: [
      "Fractional HR leadership",
      "Change & transition",
      "Strategic planning",
      "Organizational design",
      "Labor & employee relations",
      "Executive & board counsel",
    ],
    subServices: [
      ["Fractional HR Leadership", "Embedded senior People leadership on a defined cadence — without adding a full-time role."],
      ["Change Management Support", "Steady counsel through restructures, growth, mergers, and leadership transitions."],
      ["Strategic Planning Support", "People and organizational strategy shaped alongside the executive team."],
      ["Organizational Design & Structuring", "Structures, roles, and reporting lines designed for how the mission actually operates."],
      ["Labor & Employee Relations Advice", "Senior guidance on employee relations terrain, informed by employment-law fluency."],
    ],
    boundary:
      "This pillar is counsel at the decision level. When a matter needs hands-on execution — an investigation, a policy rewrite, a facilitated conversation — it moves to HR & People Consulting.",
    whoItsFor:
      "Executive directors, boards, and senior People leaders navigating change, growth, or organizational tension — and needing a senior outside voice with the judgment to match the stakes.",
    engagementShape: [
      "Discovery of the current people, leadership, and organizational terrain",
      "Advisory cadence shaped to the moment — standing counsel or defined-situation support",
      "Working sessions with the executive team, board committee, or People function as scoped",
      "Written recommendations and playbooks the internal team can actually run",
    ],
    detailIntro:
      "When organizations move — through growth, restructure, transition, or strain — the people decisions carry the most weight and the least margin for error. This advisory practice puts senior judgment beside the leaders making them.",
  },
  {
    n: "03",
    slug: "management-training-workshops",
    label: "Management Training & Workshops",
    short: "Training & Workshops",
    problem: "Managers need more capability and confidence than the role has given them.",
    outcome:
      "Practical training that gives people managers the judgment, language, and confidence the role requires.",
    focusAreas: [
      "People manager training",
      "Supervisory fundamentals",
      "Difficult conversations",
      "Leadership team workshops",
      "Retreat facilitation",
      "Custom learning series",
    ],
    subServices: [
      ["People Manager Training", "Practical, HR-aware training built around the real conversations managers are avoiding."],
      ["Leadership & Development Workshops", "Facilitated sessions for leadership teams, cohorts, and retreats — substantive, not generic."],
    ],
    boundary:
      "This pillar builds group capability in sessions. For sustained one-to-one or intact-team work over time, see Coaching for Leaders & Teams.",
    whoItsFor:
      "Organizations whose managers were promoted on competence but never trained for the conversations the role requires — and leadership teams that want capability built, not just content delivered.",
    engagementShape: [
      "Assessment of current management capability and exposure",
      "Training or workshop series designed for your organization — not off the shelf",
      "Real-conversation practice on the situations your managers actually face",
      "A written playbook the organization keeps after the engagement ends",
    ],
    detailIntro:
      "Most managers were promoted for being good at the work — not for being ready to lead people through it. This practice builds the judgment, language, and rehearsed confidence the role actually requires.",
  },
  {
    n: "04",
    slug: "coaching-leaders-teams",
    label: "Coaching for Leaders & Teams",
    short: "Coaching",
    problem: "A leader or team needs a confidential, strengths-based thinking partner.",
    outcome:
      "Strengths-based coaching — grounded in Gallup CliftonStrengths — for leaders and teams doing consequential work.",
    focusAreas: [
      "Executive coaching",
      "Team coaching",
      "CliftonStrengths",
      "Career transitions",
      "New-leader onboarding",
      "Strengths-based culture",
    ],
    subServices: [
      ["Strengths-based Executive Leadership Coaching", "Confidential 1:1 coaching for executives carrying complex people and leadership responsibilities."],
      ["Strengths-based Team Coaching", "Team coaching that builds shared language, trust, and complementary ways of working."],
      ["Strengths-based Career & Life Coaching", "Coaching for leaders navigating career inflection points and what comes next."],
      ["Strengths-based Culture & Staff Development", "CliftonStrengths applied across teams and staff cohorts — a shared language for how people work best."],
    ],
    boundary:
      "This pillar is sustained, confidential, one-to-one or intact-team work. When the need is a session for a manager cohort, see Management Training & Workshops.",
    whoItsFor:
      "Executives, rising leaders, and intact teams who want a confidential, strengths-based thinking partner — delivered by a Professional Certified Coach with Gallup CliftonStrengths training.",
    engagementShape: [
      "A confidential intake conversation and CliftonStrengths assessment",
      "A coaching cadence shaped to the leader or team — typically monthly or bi-weekly",
      "Between-session thinking partnership as agreed",
      "Periodic review of progress against what the engagement set out to shift",
    ],
    detailIntro:
      "The strongest leaders are not the most corrected — they are the most deliberately built on what already works. This coaching practice is strengths-based, confidential, and grounded in Gallup CliftonStrengths methodology.",
  },
];

export const pillarSlugToIndex6 = Object.fromEntries(pillars6.map((p, i) => [p.slug, i]));

/* -------------------------------------------------------------
 * SECTION 4 — Why Faith 1st Consulting (credibility + founder)
 * ----------------------------------------------------------- */
export const whyFirm6 = {
  eyebrow: "Why Faith 1st Consulting",
  headline: "Judgment built for consequential rooms.",
  signals: [
    { label: "Senior judgment", body: "People, leadership, HR, and organizational judgment at the level the decision actually sits." },
    { label: "High-stakes experience", body: "Two decades of practice in confidential, consequential environments — boards, executive teams, sensitive matters." },
    { label: "Practical & discreet", body: "Human-centered support that respects the room, moves the situation, and leaves capability behind." },
    { label: "Mission fluency", body: "Built for mission-driven, values-led, and public-sector-adjacent organizations — the culture is not a foreign country." },
  ],
  founder: {
    eyebrow: "Founder & Principal",
    line: "Founded and led by Katrina Bernard, JD, PCC.",
    statement:
      "Katrina Bernard brings two decades of experience across employment law, senior HR practice, executive coaching, and sensitive facilitation — judgment shaped inside organizations, not observed from a distance.",
    cta: { label: "About the founder", href: "/about-katrina#founder" },
  },
};

/* -------------------------------------------------------------
 * SECTION 5 — Resources (3 public categories)
 * ----------------------------------------------------------- */
export const resourceCategories6 = [
  { id: "insights", label: "Insights", dek: "Longer advisory material and expert perspectives from the practice." },
  { id: "guides-tools", label: "Guides & Tools", dek: "Practical how-tos, templates, and free resources leaders can use today." },
  { id: "field-notes", label: "Field Notes", dek: "Anonymized practice-based stories, lessons, and case material." },
];

export const resourcesSection6 = {
  eyebrow: "Resources",
  headline: "A working library for leaders who carry the people side.",
  cta: { label: "Explore Resources", href: "/resources" },
};

/* -------------------------------------------------------------
 * SECTION 6 — Process (four boxed steps; reused on Contact)
 * ----------------------------------------------------------- */
export const process6 = {
  eyebrow: "Process",
  headline: "Four steps from first inquiry to work beginning.",
  steps: [
    { n: "01", label: "Confidential inquiry", body: "Share a brief overview of the situation and the support you're looking for." },
    { n: "02", label: "Initial fit conversation", body: "Talk through context, priorities, and whether Faith 1st Consulting is the right fit." },
    { n: "03", label: "Scoping", body: "Define the engagement — deliverables, sequence, and rhythm of the work." },
    { n: "04", label: "Work begins", body: "Practical, discreet support aligned to the organization's needs." },
  ],
  cta: { label: "Book a consultation", href: "/contact" },
};

/* -------------------------------------------------------------
 * SECTION 7 — Final CTA
 * ----------------------------------------------------------- */
export const finalCta6 = {
  eyebrow: "Start a confidential conversation",
  headline: "Bring the situation into view.",
  body:
    "Every inquiry is confidential and reviewed personally. You will hear back within two business days — with a proposed time to talk, or a thoughtful referral if the practice is not the right fit.",
  primaryCta: { label: "Book a consultation", href: "/contact" },
  secondaryLabel: "Write us directly",
};

/* -------------------------------------------------------------
 * About — firm first, then founder, then speaking
 * ----------------------------------------------------------- */
export const aboutFirm6 = {
  eyebrow: "About",
  title: "About Faith 1st Consulting",
  lede:
    "A Leadership, People & Culture advisory firm for mission-driven, values-led, public-sector-adjacent, and mid-sized organizations — and the leaders who carry them.",
  meta: [
    ["Practice", "Leadership, People & Culture Advisory"],
    ["Serves", "Mission-driven & values-led organizations"],
    ["Based", "Washington, D.C. area"],
    ["Reach", "United States"],
  ],
  what: {
    label: "What the practice is",
    body:
      "Faith 1st Consulting sits at the intersection of employment law, senior HR judgment, certified executive coaching, and sensitive facilitation — held as one consulting lens rather than four separate specialities. The firm is brought in for the people, leadership, and workplace moments that cannot be managed through policy alone.",
  },
  who: {
    label: "Who it serves",
    body: "Executive directors, boards, senior People leaders, and the managers beneath them — inside:",
    list: [
      "Mission-driven nonprofits",
      "Foundations & philanthropy",
      "Associations & membership bodies",
      "Faith-based institutions",
      "Public-sector-adjacent bodies",
      "Values-led, mid-sized employers",
    ],
  },
  principlesEyebrow: "How the firm works",
  principlesHeadline: "Six principles every engagement is held to.",
  principles: [
    { label: "Confidential by default", body: "Every inquiry and engagement is held in confidence. Nothing leaves the practice without written permission." },
    { label: "Senior throughout", body: "The principal leads the work from first conversation to close — no hand-off to a junior bench." },
    { label: "Scoped in writing", body: "Deliverables, sequence, cadence, and boundaries are agreed before work begins." },
    { label: "Fair and defensible", body: "Findings, policies, and recommendations are built to hold up — to a board, a regulator, or a courtroom." },
    { label: "Practical over performative", body: "The measure is whether the situation moved, not whether the deck was impressive." },
    { label: "Capability left behind", body: "Engagements end with the internal team more able to hold the next moment themselves." },
  ],
};

export const aboutKatrina6 = {
  eyebrow: "About the Founder",
  title: "Katrina Bernard, JD, PCC",
  lede:
    "Founder and principal of Faith 1st Consulting — a licensed attorney, Professional Certified Coach, Gallup-trained CliftonStrengths practitioner, and senior HR leader with two decades of interior organizational experience.",
  meta: [
    ["Role", "Founder & Principal"],
    ["Credentials", "JD · PCC · Gallup-trained"],
    ["Location", "Washington, D.C. area"],
  ],
  bio: [
    "Katrina spent two decades inside organizations before founding Faith 1st Consulting. She has served as senior HR counsel, chief of staff, and senior-level consultant across mission-driven nonprofits, foundations, associations, faith-based institutions, public-sector-adjacent bodies, and values-led employers.",
    "Her interior experience — held with discretion — is what the practice is built on. She has sat in the rooms where a people decision was about to be made badly, and in the rooms where it was made well, and she knows what separated them.",
    "Katrina writes and speaks on the specific terrain most organizational buyers navigate without shared vocabulary: supervisory readiness, conduct and culture work, leadership transitions under strain, and the architecture of a People function that behaves like it belongs inside the institution.",
  ],
  credentials: [
    ["Juris Doctor (JD)", "Employment law focus"],
    ["Professional Certified Coach (PCC)", "ICF-credentialed"],
    ["Gallup-trained CliftonStrengths", "Strengths-based coaching practice"],
    ["Senior HR practice", "Two decades of interior leadership"],
    ["Board & governance consulting", "Mission-driven organizations"],
    ["Sensitive facilitation training", "Culture, conduct, and trust work"],
  ],
  approachEyebrow: "Approach",
  approach: [
    { label: "Law-informed, not law-led", body: "Employment-law fluency frames the risk; it does not replace judgment about people." },
    { label: "Strengths-based", body: "Leaders and teams are built on what already works, then stretched — not corrected into someone else." },
    { label: "Warm in the room, composed under pressure", body: "Sensitive matters are handled with care for the people involved and rigor for the organization." },
  ],
  speaking: {
    eyebrow: "Speaking & Keynotes",
    headline: "Substantive talks for rooms holding real questions.",
    intro:
      "Katrina speaks and facilitates for boards, leadership teams, associations, and mission-aligned convenings. Every talk is scoped for the specific room — its questions, its stakes, and its context. No recycled keynotes.",
    topics: [
      ["Leading Through the Moments Policy Can't Reach", "What steady leadership looks like when the handbook runs out."],
      ["The Manager's Conversation", "Building supervisory confidence for the conversations that actually decide culture."],
      ["Culture Is a Practice, Not a Poster", "How values-led organizations keep culture real under pressure and growth."],
      ["Strengths-Based Leadership for Mission-Driven Teams", "Gallup CliftonStrengths applied to how real teams lead and deliver."],
      ["Handling Sensitive Workplace Matters with Care and Rigor", "Fairness, discretion, and defensibility — held together, not traded off."],
    ],
    audiences: [
      "Boards and executive teams",
      "Association and sector convenings",
      "HR and People leaders",
      "Manager cohorts and staff retreats",
    ],
    formats: [
      "Keynote (30 – 60 minutes)",
      "Half-day or full-day workshop",
      "Board or leadership retreat facilitation",
      "Fireside or moderated conversation",
    ],
    signals: [
      "JD · PCC · Gallup-trained CliftonStrengths",
      "Two decades of senior organizational practice",
      "Content built for the room — never off the shelf",
      "Discretion appropriate for sensitive audiences",
    ],
    booking: {
      label: "Booking",
      body: "Share the event, audience, date, and the question the room is holding. You will hear back within two business days with fit, availability, and next steps.",
    },
    cta: { label: "Make a speaking inquiry", href: "/contact?area=speaking" },
  },
};

/* -------------------------------------------------------------
 * Contact / qualifying inquiry
 * ----------------------------------------------------------- */
export const inquiry6 = {
  eyebrow: "Contact",
  headline: "Book a consultation.",
  intro:
    "Tell us — at a high level — what is happening and the support you are looking for. The form is built so that by the time you submit, it is clear what you are reaching out about. Sensitive incident detail belongs in the first conversation, not here.",
  processEyebrow: "What happens after you submit",
  formEyebrow: "Confidential inquiry · 8 fields",
  writeDirectly: {
    label: "Write us directly",
    body: "Prefer email? Reach the firm's general inbox. Messages are read by the principal and answered within two business days.",
  },
  assurances: [
    { n: "01", label: "Held in confidence", body: "Every inquiry is reviewed by the principal directly. Nothing is shared beyond the practice without written permission." },
    { n: "02", label: "No incident detail required", body: "Names, incidents, and sensitive context belong in the first conversation, not this form." },
    { n: "03", label: "Response within two business days", body: "A proposed time to talk — or a thoughtful referral if the practice is not the right fit." },
  ],
};

export const serviceAreas6 = pillars6
  .map((p) => p.label)
  .concat(["Speaking & Keynotes", "Not yet sure — exploring fit"]);

export const contactMethods6 = ["Email", "Phone", "Video call"];
